import React, { useEffect, useRef } from 'react';
import { ScrollView, View, Image as ReactImage } from 'react-native';
import PlateImage from '../../../components/lpr/PlateImage';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faCarSide, faClock, faGaugeHigh } from '@fortawesome/free-solid-svg-icons';
import { translate } from '../../../services/translate';
import moment from 'moment';
import DragAndZoom from '../../../components/DragAndZoom';

interface Props {
    detection: LprDetection;
    event: CloneEvent;
}

export default function CloneDetectionModal({ detection, event }: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const canvasRefDetection = useRef<HTMLCanvasElement>(null);

    function drawImageOnCanvas(canvasRef: React.RefObject<HTMLCanvasElement>, detection: LprDetection) {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        if (!event) {
            return;
        }

        const image = new Image();

        image.onload = () => {
            canvas.width = image.width;
            canvas.height = image.height;
            ctx.drawImage(image, 0, 0);
            ctx.strokeStyle = 'red';
            ctx.lineWidth = 4;

            const rect = {
                minX: Infinity,
                maxX: 0,
                minY: Infinity,
                maxY: 0
            };

            if (detection.platePosition) {
                for (const position of detection.platePosition) {
                    if (position.x < rect.minX) {
                        rect.minX = position.x;
                    }

                    if (position.x > rect.maxX) {
                        rect.maxX = position.x;
                    }

                    if (position.y < rect.minY) {
                        rect.minY = position.y;
                    }

                    if (position.y > rect.maxY) {
                        rect.maxY = position.y;
                    }
                }

                ctx.beginPath();
                ctx.moveTo(rect.minX, rect.minY);
                ctx.lineTo(rect.minX, rect.maxY);
                ctx.lineTo(rect.maxX, rect.maxY);
                ctx.lineTo(rect.maxX, rect.minY);
                ctx.closePath();
                ctx.stroke();
            }

        };

        image.src = detection.imageUrl;
    }

    useEffect(() => {
        if (!event) {
            return;
        }
        drawImageOnCanvas(canvasRefDetection, detection);
    }, [event]);

    return (
        <View style={styles.formContainer}>
            <View>
                <DragAndZoom>
                    <canvas style={{ width: '100%' }} ref={canvasRefDetection} />
                </DragAndZoom>
                <View style={styles.detectionPlate}>
                    <View style={styles.detectionPlateTopBar}>
                        <ReactImage style={styles.plateBrazilFlag} source={require(`../../../../assets/brazil-flag.png`)} />
                    </View>
                    <View style={styles.plateContent}>
                        <MyAppText style={styles.plateLetters}>
                            {detection.plate}
                        </MyAppText>
                    </View>
                </View>
            </View>
            <View style={styles.detectionIconsContainer}>
                <PlateImage
                    width={200}
                    height={80}
                    src={detection.imageUrl}
                    platePosition={detection.platePosition}
                />
            </View>
            <View style={styles.detectionIconsContainer}>
                <View style={styles.detectionIcon}>
                    <FontAwesomeIcon size='2x' color={theme.colors.secondaryButton.text} icon={faCarSide} />
                    <MyAppText style={{ color: theme.colors.secondaryButton.text }}>{detection['vehicleDescription.type']}</MyAppText>
                </View>
                <View style={styles.detectionIcon}>
                    <FontAwesomeIcon size='2x' color={theme.colors.secondaryButton.text} icon={faGaugeHigh} />
                    <MyAppText style={{ color: theme.colors.secondaryButton.text }}>{`${event.speed}km/h`}</MyAppText>
                </View>
                <View style={styles.detectionIcon}>
                    <FontAwesomeIcon size='2x' color={theme.colors.secondaryButton.text} icon={faCalendar} />
                    <MyAppText style={{ color: theme.colors.secondaryButton.text }}>{moment(detection.time).format('DD/MM/YYYY')}</MyAppText>
                </View>
                <View style={styles.detectionIcon}>
                    <FontAwesomeIcon size='2x' color={theme.colors.secondaryButton.text} icon={faClock} />
                    <MyAppText style={{ color: theme.colors.secondaryButton.text }}>{moment(detection.time).format('HH:mm:ss')}</MyAppText>
                </View>
            </View>
            <ScrollView contentContainerStyle={styles.detectionInformationContainer}>
                <View>
                    <MyAppText style={styles.detectionTitle}>{translate('captureLocation')}</MyAppText>
                    <MyAppText style={styles.detectionValue}>{detection.camera?.title}</MyAppText>
                </View>
                <View>
                    <MyAppText style={styles.detectionTitle}>{translate('brand')}</MyAppText>
                    <MyAppText style={styles.detectionValue}>{detection['vehicleDescription.brand']}</MyAppText>
                </View>
                <View>
                    <MyAppText style={styles.detectionTitle}>{translate('model')}</MyAppText>
                    <MyAppText style={styles.detectionValue}>{detection['vehicleDescription.model']}</MyAppText>
                </View>
                <View>
                    <MyAppText style={styles.detectionTitle}>{translate('color')}</MyAppText>
                    <MyAppText style={styles.detectionValue}>{detection['vehicleDescription.color']}</MyAppText>
                </View>
                <View>
                    <MyAppText style={styles.detectionTitle}>{translate('modelYear')}</MyAppText>
                    <MyAppText style={styles.detectionValue}>{detection['vehicleDescription.modelYear']}</MyAppText>
                </View>
            </ScrollView>
        </View >
    );
}

const styleSheet = createStyleSheet(theme => ({
    formContainer: {
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 8,
        maxWidth: 370,
        maxHeight: 685,
        gap: 20
    },
    detectionPlate: {
        borderColor: 'black',
        borderWidth: 2,
        borderRadius: 8,
        minHeight: 62
    },
    detectionPlateTopBar: {
        height: 14,
        width: '100%',
        backgroundColor: '#0F3489',
        alignItems: 'flex-end',
        justifyContent: 'center',
        borderTopLeftRadius: 5,
        borderTopRightRadius: 5,
        paddingHorizontal: 15
    },
    detectionIcon: {
        flexDirection: 'column',
        gap: 3,
        justifyContent: 'center',
        alignItems: 'center'
    },
    plateBrazilFlag: {
        height: 10,
        width: 14,
        overflow: 'hidden',
        borderRadius: 1
    },
    detectionIconsContainer: {
        justifyContent: 'center',
        alignItems: 'center',
        gap: 24,
        flexDirection: 'row'
    },
    plateContent: {
        justifyContent: 'center',
        alignItems: 'center',
        flex: 1
    },
    detectionInformationContainer: {
        gap: 16,
        paddingHorizontal: 8
    },
    detectionTitle: {
        color: theme.colors.sidebar.item.selectedBackground,
        fontWeight: 'bold',
        fontSize: 16
    },
    detectionValue: {
        color: theme.colors.disabledText,
        fontSize: 14
    },
    plateLetters: {
        fontWeight: '700',
        fontSize: 24,
        letterSpacing: 8
    }
}));
